import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import React, { useState, useMemo } from "react";
import SignIn from "./auth/SignIn";
import AuthChecker from "./auth/AuthChecker";
import SignUp from "./auth/SignUp";
import LandingPage from "./components/LandingPage";
import Home from "./home/MainContainer";
import HomeMobile from "./home/MainContainerMobile";
import LibraryContainer from "./library/LibraryContainer";
import SettingsContainer from "./settings/SettingsContainer";
import SchedulerContainer from "./dashboard/SchedulerContainer";
import { I18nextProvider } from "react-i18next";
import i18n from "./i18n";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import FeedbackContainer from "./feedback/FeedbackContainer";
import OnboardingStepOne from "./onboarding/OnboardingStepOne";
import OnboardingStepTwo from "./onboarding/OnboardingStepTwo";
import Checkout from "./auth/stripe/Checkout";
import PaymentIssue from "./auth/stripe/PaymentIssue";
import PhysioVideoSelection from "./physio/PhysioVideoSelection";
import UploadComponent from "./upload/UploadComponent";
import VerifyComponent from "./auth/verify/VerifyComponent";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import OverallTour from "./dashboard/OverallTour";
import ResetPassword from "./auth/PasswordReset";

import TermsPage from "./footers/TermsPage";
import AdditionalTermsPage from "./footers/AdditionalTermsPage";
import PrivacyPolicyPage from "./footers/PrivacyPolicyPage";
import CookiePolicyPage from "./footers/CookiePolicyPage";
import AboutUs from "./headers/AboutUs";
import ProductParticipantPage from "./headers/preventive_course/ProductParticipantPage";
import HelpFAQPage from "./headers/HelpFAQPage";
import ImprintPage from "./footers/ImprintPage";
import { useFontSizeStore } from "./stores/zustand";
import { CircularProgress } from "@mui/material";
import CheckoutSuccess from "./auth/stripe/CheckoutSuccess";
import ConfirmNewPassword from "./auth/ConfirmNewPassword";
import RequirePaidAccess from "./auth/stripe/RequirePaidAccess";
import RequireHomeAccess from "./auth/RequireHomeAccess";
import RequirePhysioAccess from "./auth/RequirePhysioAccess";
import CookieBanner from "./components/cookie_banner/CookieBanner";
import DashboardPhysio from "./physio_dashboard/DashboardPhysio";

const theme = createTheme({
  typography: {
    fontFamily: "Poppins, sans-serif",
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          backgroundColor: "#063bd0",
          color: "#fff",
          textTransform: "none",
          "&:hover": {
            backgroundColor: "#05b67f",
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: "transparent",
          boxShadow: "none",
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: "transparent",
          color: "white",
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        switchBase: {
          "&.Mui-checked": {
            color: "#063bd0",
          },
          "&.Mui-checked + .MuiSwitch-track": {
            backgroundColor: "#05b67f",
          },
        },
        track: {
          backgroundColor: "#cdcdcd",
        },
        thumb: {
          backgroundColor: "#063bd0",
        },
      },
    },
  },
});

function useIsMobile() {
  const location = useLocation();

  // Memoize the path check to prevent re-calculations
  const shouldCheckUserAgent = useMemo(() => {
    const path = location.pathname;
    const excludedPaths = [
      "/physio-video-selection",
      "/register-physio",
      "/",
      "/register",
      "/corporate",
      "/course",
      "/register-corporate",
      "/register-course",
      "/register-physio-user",
    ];

    return !excludedPaths.includes(path);
  }, [location.pathname]);

  // Memoize the mobile detection result
  return useMemo(() => {
    if (!shouldCheckUserAgent) return false;

    const toMatch = [
      /Android/i,
      /webOS/i,
      /iPhone/i,
      /iPad/i,
      /iPod/i,
      /BlackBerry/i,
      /Windows Phone/i,
    ];

    return toMatch.some((toMatchItem) =>
      navigator.userAgent.match(toMatchItem),
    );
  }, [shouldCheckUserAgent]);
}

if (window.location.hostname !== "localhost") {
  console.log = () => {};
  console.debug = () => {};
  console.info = () => {};
  console.warn = () => {};
  console.error = () => {};
}

function App() {
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const isMobile = useIsMobile();
  const { fontSize } = useFontSizeStore();

  return (
    <div style={{ fontSize: `${fontSize}px` }}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <ThemeProvider theme={theme}>
          <I18nextProvider i18n={i18n}>
            <AuthChecker setIsCheckingAuth={setIsCheckingAuth} />
            {isCheckingAuth ? (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100vh",
                  width: "100vw",
                }}
              >
                <CircularProgress size={60} />
              </div>
            ) : (
              <Routes>
                <Route path="/" element={<LandingPage />} />
                <Route path="/login" element={<Navigate to="/" replace />} />
                <Route path="corporate" element={<SignIn />} />
                <Route path="course" element={<SignIn />} />
                <Route path="physio" element={<SignIn />} />
                <Route path="physio-user" element={<SignIn />} />
                <Route path="verify" element={<VerifyComponent />} />
                <Route
                  path="confirm-new-password"
                  element={<ConfirmNewPassword />}
                />
                <Route
                  path="physio-video-selection"
                  element={<PhysioVideoSelection />}
                />
                {/* Physio Dashboard Route */}
                <Route element={<RequirePhysioAccess />}>
                  <Route path="physio-dashboard" element={<DashboardPhysio />} />
                </Route>

                {/* Main App Container with Nested Routes */}
                <Route element={<RequireHomeAccess />}>
                  <Route
                    path="home"
                    element={isMobile ? <HomeMobile /> : <Home />}
                  >
                    {/* Default redirect */}
                    <Route index element={<Navigate to="calendar" replace />} />

                    {/* Nested routes - render inside Home's <Outlet /> */}
                    <Route path="calendar" element={<SchedulerContainer />} />
                    <Route
                      path="schedule"
                      element={<Navigate to="/home/calendar" replace />}
                    />
                    <Route path="feedback" element={<FeedbackContainer />} />
                    <Route path="settings" element={<SettingsContainer />} />
                    <Route path="library" element={<LibraryContainer />} />
                    {/* <Route path="test" element={<LocalNotificationTest />} /> */}
                  </Route>
                </Route>

                {/* Other routes */}
                <Route path="register" element={<SignUp />} />
                <Route path="register-corporate" element={<SignUp />} />
                <Route path="register-course" element={<SignUp />} />
                <Route path="register-physio" element={<SignUp />} />
                <Route path="register-physio-user" element={<SignUp />} />
                <Route
                  path="6f27feba-ba3a-43e1-9314-9e313331ef81"
                  element={<UploadComponent />}
                />
                <Route path="reset/password" element={<ResetPassword />} />
                <Route path="terms" element={<TermsPage />} />
                <Route
                  path="additional-terms"
                  element={<AdditionalTermsPage />}
                />
                <Route path="privacy-policy" element={<PrivacyPolicyPage />} />
                <Route path="cookies" element={<CookiePolicyPage />} />
                <Route path="about-us" element={<AboutUs />} />
                <Route
                  path="preventive_course-info"
                  element={<ProductParticipantPage />}
                />
                <Route path="help-faq" element={<HelpFAQPage />} />
                <Route path="imprint" element={<ImprintPage />} />

                {/* Main App Container with Nested Routes */}
                <Route element={<RequirePaidAccess />}>
                  <Route path="checkout" element={<Checkout />} />
                  <Route
                    path="checkout/success"
                    element={<CheckoutSuccess />}
                  />
                  <Route path="payment-issue" element={<PaymentIssue />} />
                  <Route
                    path="onboarding-exercises"
                    element={<OnboardingStepOne />}
                  />
                  <Route
                    path="onboarding-movement"
                    element={<OnboardingStepTwo />}
                  />
                  <Route
                    path="physio-video-selection"
                    element={<PhysioVideoSelection />}
                  />
                  <Route
                    path="home"
                    element={isMobile ? <HomeMobile /> : <Home />}
                  >
                    {/* Default redirect */}
                    <Route index element={<Navigate to="calendar" replace />} />

                    {/* Nested routes - render inside Home's <Outlet /> */}
                    <Route path="calendar" element={<SchedulerContainer />} />
                    <Route
                      path="schedule"
                      element={<Navigate to="/home/calendar" replace />}
                    />
                    <Route path="feedback" element={<FeedbackContainer />} />
                    <Route path="settings" element={<SettingsContainer />} />
                    <Route path="library" element={<LibraryContainer />} />
                    <Route
                      path="library/*"
                      element={<Navigate to="/home/library" />}
                    />
                    {/* <Route path="test" element={<LocalNotificationTest />} /> */}
                  </Route>
                </Route>
              </Routes>
            )}
            <OverallTour />
            <CookieBanner />
          </I18nextProvider>
        </ThemeProvider>
      </LocalizationProvider>
    </div>
  );
}

export default App;
